import React, { useEffect, useState } from 'react';
import './WorkspaceScene.css';

/**
 * WorkspaceScene — the "Workday Clock".
 *
 * The twelve modules sit at the twelve hour positions of a live clock around a
 * central face that shows the current time and the module "on the clock" right
 * now (its tile is highlighted and a hand points to it). Minute ticks fill in teal
 * as the day progresses. Presentational only — a preview before sign-in; routes
 * and permissions are unchanged. The face keeps itself current on a light timer.
 */

interface Mod { name: string; tag: string; icon: React.ReactNode; }

// In clock order: index 0 = 12 o'clock, 1 = 1 o'clock, … 11 = 11 o'clock.
const MODULES: Mod[] = [
  { name: 'Ask Creyon', tag: 'Ask anything, anytime', icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /> },
  { name: 'Expenses', tag: 'Submit & track claims', icon: <><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M16 13h2M2 10h20" /></> },
  { name: 'Performance', tag: 'Goals, reviews, growth', icon: <><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></> },
  { name: 'Voting Polls', tag: 'Have your say', icon: <><path d="M9 11l3 3 8-8" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" /></> },
  { name: 'Award Nominations', tag: 'Celebrate great work', icon: <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" /> },
  { name: 'Timesheets', tag: 'Log your hours', icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></> },
  { name: 'Certifications', tag: 'Keep skills current', icon: <><circle cx="12" cy="9" r="6" /><path d="M8.5 14L7 22l5-3 5 3-1.5-8" /></> },
  { name: 'Employee Profiles', tag: 'Keep your details up to date', icon: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="11" r="2.5" /><path d="M5.5 17a3.5 3.5 0 0 1 7 0M15 9h3M15 13h3" /></> },
  { name: 'Teams & Users', tag: 'Your people, organized', icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></> },
  { name: 'Attendance', tag: 'Punch in and out', icon: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></> },
  { name: 'Leave', tag: 'Plan your time off', icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M9 16l2 2 4-4" /></> },
  { name: 'Shift Roster', tag: 'See your schedule', icon: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18M9 4v18" /></> },
];

const R = 36; // module ring radius (% of the square box)

const pos = (hourIndex: number, radius: number) => {
  const a = (hourIndex * 30) * (Math.PI / 180);
  return { x: 50 + radius * Math.sin(a), y: 50 - radius * Math.cos(a) };
};

const Glyph: React.FC<{ icon: React.ReactNode; size?: number }> = ({ icon, size = 21 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{icon}</svg>
);

export const WorkspaceScene: React.FC = () => {
  const [now, setNow] = useState(() => new Date());
  // Which module is hovered/focused; null → the center follows the clock hand.
  const [hovered, setHovered] = useState<number | null>(null);
  // The second hand sweeps via CSS; capture the mount offset once so re-renders
  // never restart its animation.
  const [secOffset] = useState(() => new Date().getSeconds() + new Date().getMilliseconds() / 1000);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const h24 = now.getHours();
  const m = now.getMinutes();
  const s = now.getSeconds();
  const h12 = ((h24 + 11) % 12) + 1;           // 1..12
  const ampm = h24 < 12 ? 'am' : 'pm';
  const timeStr = `${h12}:${String(m).padStart(2, '0')}`;

  const currentIndex = h12 % 12;               // 0..11 (12 o'clock -> 0)
  const hourDeg = (h12 % 12 + m / 60) * 30;    // hour hand
  const minDeg = (m + s / 60) * 6;             // minute hand
  const tip = (deg: number, len: number) => ({
    x: 50 + len * Math.sin(deg * Math.PI / 180),
    y: 50 - len * Math.cos(deg * Math.PI / 180),
  });
  const hourTip = tip(hourDeg, 27);
  const minTip = tip(minDeg, 33);

  // The module shown in the centre: hovered one, else the current hour.
  const shownIndex = hovered ?? currentIndex;
  const shown = MODULES[shownIndex];
  const shownHour = shownIndex === 0 ? 12 : shownIndex;

  return (
    <div className="scene-wrap">
      <div className="scene" role="img" aria-label={`CreyenteOne workday clock. It is ${timeStr} ${ampm}; right now: ${MODULES[currentIndex].name}.`}>
        <span className="scene__glow" aria-hidden="true" />

        <svg className="scene__orbit" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          {/* Minute ticks — elapsed ones fill teal with the minute hand. */}
          {Array.from({ length: 60 }, (_, i) => {
            const deg = i * 6;
            const a = deg * (Math.PI / 180);
            const major = i % 5 === 0;
            const rOut = 46;
            const rIn = major ? 42.5 : 44;
            const elapsed = deg <= minDeg;
            return (
              <line
                key={i}
                className={`scene__tick ${major ? 'is-major' : ''} ${elapsed ? 'is-elapsed' : ''}`}
                x1={50 + rIn * Math.sin(a)} y1={50 - rIn * Math.cos(a)}
                x2={50 + rOut * Math.sin(a)} y2={50 - rOut * Math.cos(a)}
              />
            );
          })}
          {/* Three hands: hour (short/thick), minute (longer), and a continuously
              sweeping second hand — all long enough to clear the clock face.
              Hour/minute use direct geometry; the second hand sweeps via CSS. */}
          <line className="scene__hand-line scene__hand-line--hour" x1="50" y1="50" x2={hourTip.x} y2={hourTip.y} />
          <line className="scene__hand-line scene__hand-line--min" x1="50" y1="50" x2={minTip.x} y2={minTip.y} />
          <g className="scene__hand scene__hand--sec" style={{ animationDelay: `-${secOffset}s` }}>
            <line x1="50" y1="55" x2="50" y2={50 - 34} />
          </g>
          <circle className="scene__hand-hub" cx="50" cy="50" r="1.5" />
        </svg>

        {/* Central clock face. */}
        <div className="scene__face">
          <span className="scene__face-glow" aria-hidden="true" />
          <span className="scene__face-surface">
            <span className="scene__time"><b>{timeStr}</b><i>{ampm}</i></span>
            <span className="scene__now">{hovered === null ? 'Right now' : `${String(shownHour).padStart(2, '0')}:00`}</span>
            <span className="scene__now-mod">{shown.name}</span>
            <span className="scene__now-tag">{shown.tag}</span>
          </span>
        </div>

        {/* Twelve module tiles at the hour positions. */}
        {MODULES.map((mod, i) => {
          const p = pos(i, R);
          const hour = i === 0 ? 12 : i;
          const active = i === currentIndex;
          return (
            <div
              key={mod.name}
              className={`scene__node ${active ? 'is-active' : ''} ${hovered === i ? 'is-hovered' : ''}`}
              style={{ left: `${p.x}%`, top: `${p.y}%`, ['--i' as string]: i }}
              tabIndex={0}
              aria-label={`${mod.name}, ${String(hour).padStart(2, '0')}:00`}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
              onFocus={() => setHovered(i)}
              onBlur={() => setHovered((h) => (h === i ? null : h))}
            >
              <span className="scene__node-time">{String(hour).padStart(2, '0')}:00</span>
              <span className="scene__tile" aria-hidden="true"><Glyph icon={mod.icon} /></span>
              <span className="scene__node-label">{mod.name}</span>
            </div>
          );
        })}
      </div>

      {/* Phone fallback: the same modules + their clock hour, as a tidy list. */}
      <ul className="scene-mods" aria-label="All modules">
        {MODULES.map((mod, i) => (
          <li className={`scene-mod ${i === currentIndex ? 'is-active' : ''}`} key={mod.name}>
            <span className="scene-mod__icon" aria-hidden="true"><Glyph icon={mod.icon} size={16} /></span>
            <span className="scene-mod__label">{mod.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default WorkspaceScene;
